import React, { FC, useMemo, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { OpenCampaignCard, OpenCampaignCardAspectRatio } from '@browsing/campaign';
import { mapFashionPreference } from '@browsing/utils';
import { TopPicks4U } from '@browsing/top-picks-4U';

import { Campaign } from '@shared/types/mylounge';
import { getCategoryTabId, getEAOpenCampaigns, getTabs } from '@client/redux/modules/mylounge';
import { getCustomerFashionPreferences, getGender } from '@client/redux/modules/customer';
import { GridBlock, GridBlockId, GridBlockType } from '@client/config/layout';
import { CategoryTabIds } from '@shared/constants';
import { readSelectedFashionPreference } from '@client/helpers';
import { getSemanticArticlesCarouselData } from '@client/redux/modules/semanticArticles';
import {
    ContinueShoppingCarousel,
    useContinueShopping,
} from '@client/components/continue-shopping';
import { getColumnCount, mergeCampaignsWithGrid, getGrid } from './helpers';
import { CustomerGender, GridBlockDimensions } from './constants';

import { Container, GridWrapper } from './styles';
import { ShowstopperGridContainer } from '../components/showstopper.styles';
import { PlusExclusiveCampaign, usePlusExclusiveCampaignPosition } from '../../plus';
import { SemanticArticleCarousel } from '../../semantic-article-carousel';
import { NewDealsCarousel } from '../../new-deals-today';

const isGridBlockCampaign = (
    gridBlock: GridBlock | (Campaign & GridBlock),
): gridBlock is Campaign & GridBlock => {
    return !(gridBlock as GridBlock).type;
};

interface Props {
    width: number;
    campaigns: Campaign[];
    isPlusEnabled: boolean;
    isNewDealsToday: boolean;
    campaignsStartingToday: Campaign[];
}

export const Grid: FC<Props> = ({
    width,
    campaigns,
    isPlusEnabled,
    isNewDealsToday,
    campaignsStartingToday,
}) => {
    const earlyAccessOpenCampaigns = useSelector(getEAOpenCampaigns);
    const profileFashionPreference = mapFashionPreference(
        useSelector(getCustomerFashionPreferences)?.[0],
    );
    const selectedFashionPreference = readSelectedFashionPreference();

    const categoryTabId = useSelector(getCategoryTabId);
    const tabs = useSelector(getTabs);
    const hasPlusOffersTab = tabs.some(tab => tab.event === 'PLUS_OFFER_EVENT');

    const numberOfEACampaignsShouldShown =
        isPlusEnabled && !hasPlusOffersTab ? earlyAccessOpenCampaigns.length : 0;
    const isPlusTab = tabs.some(
        tab => tab.event === 'PLUS_OFFER_EVENT' && tab.id === categoryTabId,
    );

    const columnCount = useMemo(() => getColumnCount(width), [width]);

    const plusExclusiveCampaignPosition = usePlusExclusiveCampaignPosition({
        columnCount,
        offset: numberOfEACampaignsShouldShown,
    });

    /**
     * Preprocess grid to add Plus campaigns such as early access campaigns and exclusive campaign
     * We are manipulating the gridBlock array to add Plus campaigns in the grid
     */
    const preprocessGrid = useCallback(
        (gridBlock: GridBlock[]): number => {
            let numberOfCampaignsAdded = 0;

            for (let i = 0; i < numberOfEACampaignsShouldShown; i++) {
                gridBlock[i].type = GridBlockType.PLUS_CAMPAIGN;
                gridBlock[i].id = GridBlockId.PLUS_CAMPAIGN_CARD;
                numberOfCampaignsAdded++;
            }

            if (
                Number.isInteger(plusExclusiveCampaignPosition) &&
                plusExclusiveCampaignPosition !== -1
            ) {
                gridBlock[plusExclusiveCampaignPosition].type = GridBlockType.PLUS_CAMPAIGN;
                gridBlock[plusExclusiveCampaignPosition].id = GridBlockId.PLUS_EXCLUSIVE_CAMPAIGN;
                numberOfCampaignsAdded++;
            }

            return numberOfCampaignsAdded;
        },
        [numberOfEACampaignsShouldShown, plusExclusiveCampaignPosition],
    );

    const { articles: semanticArticles } = useSelector(getSemanticArticlesCarouselData);
    const isSemanticCarousel = semanticArticles.length > 0;
    const hasAdditionalShowStopper = isNewDealsToday && isSemanticCarousel;

    const grid = useMemo(() => {
        return getGrid(
            categoryTabId,
            columnCount,
            campaigns.length,
            preprocessGrid,
            hasAdditionalShowStopper,
        );
    }, [categoryTabId, columnCount, campaigns.length, preprocessGrid, hasAdditionalShowStopper]);

    const campaignsWithGrid = useMemo(
        () => mergeCampaignsWithGrid(campaigns, [...grid], columnCount),
        [campaigns, grid, columnCount],
    );

    let campaignIndex = 0; // only for upcoming campaings, banner position is not counted

    const gender = useSelector(getGender)?.toUpperCase() as CustomerGender;
    const { shouldShowContinueShopping, continueShoppingData } = useContinueShopping();

    if (!campaigns) {
        return null;
    }

    const validateGridBlockDimension = (dimension: string): GridBlockDimensions => {
        if (Object.values(GridBlockDimensions).includes(dimension as GridBlockDimensions)) {
            return dimension as GridBlockDimensions;
        }
        return GridBlockDimensions.SQUARE_SMALL;
    };

    return (
        <Container>
            {categoryTabId === CategoryTabIds.ALL && (
                <GridWrapper id="campaigns-open" data-testid="campaigns-open">
                    {campaignsWithGrid.map((gridBlock, index) => {
                        const dimension = `${gridBlock.w}x${gridBlock.h}`;
                        const validatedDimension = validateGridBlockDimension(dimension);

                        if (isGridBlockCampaign(gridBlock)) {
                            return (
                                <OpenCampaignCard
                                    key={gridBlock.campaignId}
                                    aspectRatio={validatedDimension}
                                    campaign={gridBlock}
                                    grid={gridBlock}
                                    isPlusEnabled={isPlusEnabled}
                                    position={`${++campaignIndex}|${columnCount}`}
                                />
                            );
                        }

                        if (gridBlock.id === GridBlockId.PLUS_CAMPAIGN_CARD) {
                            const earlyAccessCampaign = earlyAccessOpenCampaigns[index];

                            if (!earlyAccessCampaign) {
                                return null;
                            }

                            return (
                                <OpenCampaignCard
                                    key={earlyAccessCampaign.campaignId}
                                    aspectRatio={validatedDimension}
                                    campaign={earlyAccessCampaign}
                                    grid={gridBlock}
                                    isPlusEnabled={isPlusEnabled}
                                    position={`${++campaignIndex}|${columnCount}`}
                                />
                            );
                        }

                        if (gridBlock.id === GridBlockId.PLUS_EXCLUSIVE_CAMPAIGN) {
                            return (
                                <PlusExclusiveCampaign
                                    key={gridBlock.id}
                                    aspectRatio={validatedDimension}
                                    grid={gridBlock}
                                    position={`${++campaignIndex}|${columnCount}`}
                                />
                            );
                        }

                        if (gridBlock.id === GridBlockId.SHOWSTOPPER) {
                            return (
                                <ShowstopperGridContainer
                                    key={gridBlock.id}
                                    columnCount={columnCount}
                                >
                                    <SemanticArticleCarousel />
                                    <TopPicks4U
                                        gender={gender}
                                        profileFashionPreference={profileFashionPreference}
                                        selectedFashionPreference={selectedFashionPreference}
                                    />
                                    {!isSemanticCarousel && isNewDealsToday && (
                                        <NewDealsCarousel
                                            campaignsStartingToday={campaignsStartingToday}
                                            needsTopMargin={true}
                                        />
                                    )}
                                </ShowstopperGridContainer>
                            );
                        }

                        if (gridBlock.id === GridBlockId.NEW_DEALS_CAROUSEL && isSemanticCarousel) {
                            return (
                                <ShowstopperGridContainer
                                    key={gridBlock.id}
                                    columnCount={columnCount}
                                >
                                    <NewDealsCarousel
                                        campaignsStartingToday={campaignsStartingToday}
                                    />
                                    <ContinueShoppingCarousel data={continueShoppingData} />
                                </ShowstopperGridContainer>
                            );
                        }
                        if (
                            gridBlock.id === GridBlockId.CONTINUE_SHOPPING_SHOWSTOPPER &&
                            shouldShowContinueShopping
                        ) {
                            return (
                                <ShowstopperGridContainer
                                    key={gridBlock.id}
                                    columnCount={columnCount}
                                >
                                    <ContinueShoppingCarousel data={continueShoppingData} />
                                </ShowstopperGridContainer>
                            );
                        }

                        return undefined;
                    })}
                </GridWrapper>
            )}
            {isPlusTab && (
                <GridWrapper isSimpleGrid id="campaigns-open" data-testid="campaigns-open">
                    {campaigns.map(campaign => (
                        <OpenCampaignCard
                            key={campaign.campaignId}
                            aspectRatio={OpenCampaignCardAspectRatio.Square}
                            campaign={campaign}
                            isPlusEnabled={true}
                            position={`${++campaignIndex}|${columnCount}`}
                        />
                    ))}
                </GridWrapper>
            )}
            {!isPlusTab && categoryTabId !== CategoryTabIds.ALL && (
                <GridWrapper isSimpleGrid id="campaigns-open" data-testid="campaigns-open">
                    {campaigns.map(campaign => (
                        <OpenCampaignCard
                            key={campaign.campaignId}
                            aspectRatio={OpenCampaignCardAspectRatio.Square}
                            campaign={campaign}
                            isPlusEnabled={isPlusEnabled}
                            position={`${++campaignIndex}|${columnCount}`}
                        />
                    ))}
                </GridWrapper>
            )}
        </Container>
    );
};

export default memo(Grid);
